import React, { useEffect, useState } from 'react';
import { SetLoading, PromoterContact } from '../SiteClasses';
import { useUser } from '../hooks';
import { bindFormChange, bindClick } from '../SiteUtil';
import { Table, Button, Modal, Input, Header, Pagination, Message, Toast } from 'react-frontier';
import UserAccess from '../UserAccess';
import Validator from '../Validator';
import API from '../API';

interface PromoterContactsTableProps{
	promoter_id: number;
}

interface ContactForm{
	contact_id?: number;
	contact_name: string;
	email: string;
	phone: string;
	department: string;
}

const PAGE_SIZE = 20;

var PromoterContactsTable = (props: PromoterContactsTableProps)=>{
	var { hasAccess } = useUser();
	var { promoter_id } = props;
	var [contacts, setContacts] = useState<PromoterContact[]>([]);
	var [page, setPage] = useState<number>(0);
	var [loading, setLoading] = useState(false);
	var [showModal, setShowModal] = useState(false);
	var [form, setForm] = useState<ContactForm>({ contact_name: '', email: '', phone: '', department: '' });
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		getContacts();
	}, [promoter_id, page]);

	var getContacts = (get_page?: number)=>{
		var offset = (get_page > 0) ? Math.max(0, (get_page || page) * PAGE_SIZE) : 0;

		setLoading(true);
		API.getPromoterContacts(promoter_id, offset, PAGE_SIZE).then(res => {
			if(res.error) return Toast.error(res.message);
			setContacts(res.data);
		}).catch(err=>{
			Toast.error('Hubo un error cargando los contactos (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onPageChange = (page: number)=>{
		setLoading(true);
		setPage(page);
		getContacts(page);
	}

	var onShowModal = (contact?: PromoterContact)=>{
		if(contact){
			setForm(contact);
		}else{
			setForm({ contact_name: '', email: '', phone: '', department: '' });
		}
		setShowModal(true);
	};

	var onSubmit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			contact_name: [{
				rule: 'minLength', params: [3], label: 'Nombre de contacto'
			}],
			email: [{
				rule: 'email', label: 'Correo electrónico'
			}],
			phone: [{
				rule: 'phone', label: 'Teléfono'
			}],
			department: [{
				rule: 'minLength', params: [3], label: 'Departamento'
			}],
		});
		setModalPrompts(prompts);
		if(!valid) return;  
		setLoading(true);

		if(form.contact_id){
			API.updatePromoterContact(form.contact_id, form.contact_name, form.email, form.phone, form.department).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Contacto actualizado!');
				
				var c = [...contacts];
				var i = c.findIndex(b=>b.contact_id === form.contact_id);
				c[i] = {
					promoter_id: promoter_id,
					contact_id: form.contact_id,
					contact_name: form.contact_name,
					email: form.email,
					phone: form.phone,
					department: form.department,
					deleted: false,
				};
				setContacts(c);
				setShowModal(false);
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado actualizando el contacto (LCL-1)']);
			}).finally(()=>{
				setLoading(false);
			});
		}else{
			API.createPromoterContact(props.promoter_id, form.contact_name, form.email, form.phone, form.department).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Contacto agregado!');

				var c = [...contacts];
				c.push({
					promoter_id: promoter_id,
					contact_id: res.data.contact_id,
					contact_name: form.contact_name,
					email: form.email,
					phone: form.phone,
					department: form.department,
					deleted: false,
				});
				setContacts(c);
				setShowModal(false);
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado creando el contacto (LCL-2)']);
			}).finally(()=>{
				setLoading(false);
			});
		}
	};

	var onFormChange = bindFormChange(form, setForm);

	return <>
		<Table fitted striped
			title='Contactos'
			headers={['ID', 'Nombre', 'Departamento', 'Email', 'Numero']}
			style={{ maxWidth: 800, margin: 'auto', marginTop: 20 }}
			button={hasAccess(UserAccess.EVENTS.EDIT_PROMOTERS) && (
				<Button text='Crear' color='black' iconName='plus' size='tiny' onClick={() => onShowModal()}/>
			)}
			footer={
				(!!contacts && contacts.length >= PAGE_SIZE || page !== 0) && (
					<Table.Cell colSpan={7} row>
						<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<Pagination page={page} onPageChange={onPageChange} hasNext={contacts.length>=PAGE_SIZE} />
						</div>
					</Table.Cell>
				)
			}
		>
			{loading ? (
				<Table.Row>
					<Table.Cell colSpan={7}>
						<Header loading text='Cargando datos...' />
					</Table.Cell>
				</Table.Row>
			) : contacts.length !== 0 ? (
				contacts.map(c => (
					<Table.Row
						key={c.contact_id}
						collapsingIndexes={[0,  2, 3, 4, 5, 6]}
						selectable
						centeredIndexes={[4, 5]}
						onClick={() => onShowModal(c)}
						data={[
							c.contact_id,
							c.contact_name,
							c.department,
							c.email,
							c.phone,
						]}
					/>
				))
			) : (
				<Table.Cell row className='empty' colSpan={7}>
					No se encontraron registros
				</Table.Cell>
			)}
		</Table>
		<Modal open={showModal} size="tiny" onClose={bindClick(setShowModal, false)}>
			<Modal.Header>{form.contact_id ? 'Editar contacto' : 'Crear contacto'}</Modal.Header>
			{form && <Modal.Content>
				<Input label='Nombre' value={form.contact_name} onChange={onFormChange('contact_name')} />
				<Input label='Departamento' value={form.department} onChange={onFormChange('department')} />
				<Input label='Correo electrónico' value={form.email} onChange={onFormChange('email')} />
				<Input label='Teléfono' value={form.phone} onChange={onFormChange('phone')} />
				<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button onClick={bindClick(setShowModal, false)} text='Cerrar' basic/>
				<Button color="black" onClick={onSubmit}  text='Guardar'/> 
			</Modal.Actions>
		</Modal>
	</>
};

export default PromoterContactsTable;
